import type { CreateJobInput } from '@kiku/jobs/types';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DepartmentBreadcrumb } from '~/components/features/departments/department-breadcrumb';
import { CATEGORY_DISPLAY_ORDER } from '~/components/features/interviews/interviews.types';
import type { WizardQuestion } from '~/components/features/interviews/interviews.types';
import { useRegenerateScoring } from '~/components/features/interviews/use-regenerate-scoring';
import { WizardStepHeader } from '~/components/features/interviews/wizard-step-header';
import { BasicInfoStep } from '~/components/features/interviews/wizard-steps/basic-info-step';
import { ConfirmStep } from '~/components/features/interviews/wizard-steps/confirm-step';
import { QuestionsStep } from '~/components/features/interviews/wizard-steps/questions-step';
import { PageContainer } from '~/components/layouts/page-container';
import { Button } from '~/components/ui/button';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { api } from '~/lib/api';
import { i18n } from '~/lib/i18n';
import { orgSettingsQueryOptions } from '~/queries/org-settings.queries';

export const Route = createFileRoute('/_authenticated/org/$orgSlug/department/$deptSlug/jobs/new/')(
  {
    component: NewJobWizard
  }
);

type WizardState = {
  step: 1 | 2 | 3;
  jobId: string | null;
  basicInfo: CreateJobInput | null;
  questions: WizardQuestion[];
};

type WizardContextType = WizardState & {
  setState: React.Dispatch<React.SetStateAction<WizardState>>;
  orgId: string;
  deptId: string;
  orgSlug: string;
  deptSlug: string;
};

const WizardContext = createContext<WizardContextType | null>(null);

function useWizard(): WizardContextType {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error('useWizard must be used inside WizardContext');
  return ctx;
}

function Step1(): React.ReactNode {
  const { t } = useTranslation();
  const { setState, orgId, deptId, basicInfo } = useWizard();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: orgSettings } = useQuery(orgSettingsQueryOptions(orgId));

  async function handleSubmit(data: CreateJobInput): Promise<void> {
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await api.api.org[':orgId'].department[':deptId'].jobs.$post({
        param: { orgId, deptId },
        json: data
      });
      const { job } = await res.json();
      setState((prev) => ({
        ...prev,
        step: 2,
        jobId: job.id,
        basicInfo: data,
        questions: []
      }));
    } catch {
      setError(t('jobs.createError'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      {error && (
        <div className="mb-4 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}
      <BasicInfoStep
        defaultValues={{
          communicationLanguage: orgSettings?.language ?? undefined,
          ...(basicInfo ?? {})
        }}
        onSubmit={(d) => void handleSubmit(d)}
        isSubmitting={isSubmitting}
      />
    </>
  );
}

function Step2(): React.ReactNode {
  const { t } = useTranslation();
  const { setState, jobId, questions, orgId, deptId } = useWizard();
  const [isGenerating, setIsGenerating] = useState(true);
  const [generateError, setGenerateError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const generateQuestions = useCallback(async (): Promise<void> => {
    if (!jobId) return;
    setIsGenerating(true);
    setGenerateError(false);
    try {
      const res = await api.api.org[':orgId'].department[':deptId'].jobs[':jobId'][
        'interview-setup'
      ].questions.generate.$post({ param: { orgId, deptId, jobId } });
      if (!res.ok) throw new Error(i18n.t('errors.unexpected'));
      const { questions: generated } = await res.json();
      setState((prev) => ({
        ...prev,
        questions: generated.map((q) => ({
          id: q.id,
          category: q.category,
          text: q.text,
          position: q.position,
          isOptional: q.isOptional,
          competencyName: q.competencyName ?? undefined,
          scoringAnchors: q.scoringAnchors ?? undefined,
          aiGenerated: q.aiGenerated
        }))
      }));
    } catch {
      setGenerateError(true);
    } finally {
      setIsGenerating(false);
    }
  }, [jobId, orgId, deptId, setState]);

  // Auto-generate on mount — skip if questions already loaded (e.g. navigating back then forward)
  useEffect(() => {
    if (questions.length > 0) {
      setIsGenerating(false);
      return;
    }
    void generateQuestions();
  }, [generateQuestions]);

  async function handleSave(): Promise<void> {
    if (!jobId) return;
    setIsSaving(true);
    try {
      const normalizedQuestions = CATEGORY_DISPLAY_ORDER.flatMap((category) =>
        questions.filter((q) => q.category === category).sort((a, b) => a.position - b.position)
      ).map((q, position) => ({
        category: q.category,
        text: q.text,
        position,
        isOptional: q.isOptional,
        competencyName: q.competencyName,
        scoringAnchors: q.scoringAnchors
      }));
      await api.api.org[':orgId'].department[':deptId'].jobs[':jobId'][
        'interview-setup'
      ].questions.$put({
        param: { orgId, deptId, jobId },
        json: { questions: normalizedQuestions }
      });
      await api.api.org[':orgId'].department[':deptId'].jobs[':jobId']['setup-status'].$patch({
        param: { orgId, deptId, jobId },
        json: { setupStatus: 'ready' }
      });
      setState((prev) => ({ ...prev, step: 3 }));
    } finally {
      setIsSaving(false);
    }
  }

  if (generateError) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-sm text-destructive">{t('wizard.questions.generateError')}</p>
        <Button variant="outline" onClick={() => void generateQuestions()}>
          {t('wizard.questions.retry')}
        </Button>
      </div>
    );
  }

  const handleRegenerateScoring = useRegenerateScoring(orgId, deptId, jobId);

  return (
    <QuestionsStep
      questions={questions}
      isGenerating={isGenerating}
      onQuestionsChange={(qs) => {
        setState((prev) => ({ ...prev, questions: qs }));
      }}
      onBack={() => {
        setState((prev) => ({ ...prev, step: 1 }));
      }}
      onSave={() => void handleSave()}
      isSaving={isSaving}
      onRegenerateScoring={handleRegenerateScoring}
    />
  );
}

function Step3(): React.ReactNode {
  const { jobId, basicInfo, questions, orgId, deptId, orgSlug, deptSlug, setState } = useWizard();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleGoLive(): Promise<void> {
    if (!jobId) return;
    setIsSubmitting(true);
    try {
      await api.api.org[':orgId'].department[':deptId'].jobs[':jobId']['setup-status'].$patch({
        param: { orgId, deptId, jobId },
        json: { setupStatus: 'active' }
      });
      void navigate({
        to: '/org/$orgSlug/department/$deptSlug/jobs',
        params: { orgSlug, deptSlug }
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ConfirmStep
      jobs={basicInfo ? [{ title: basicInfo.title, description: basicInfo.description }] : []}
      questions={questions}
      modality={basicInfo?.modality ?? ['text']}
      languages={basicInfo?.languages ?? []}
      communicationLanguage={basicInfo?.communicationLanguage ?? 'en'}
      onBack={() => {
        setState((prev) => ({ ...prev, step: 2 }));
      }}
      onGoLive={() => void handleGoLive()}
      isSubmitting={isSubmitting}
    />
  );
}

function NewJobWizard(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t('wizard.manual.title'));
  const { orgSlug, deptSlug } = Route.useParams();
  const { orgId, deptId } = useSlugIds(orgSlug, deptSlug);

  const [state, setState] = useState<WizardState>({
    step: 1,
    jobId: null,
    basicInfo: null,
    questions: []
  });

  const steps = [
    { label: t('wizard.manual.step1Title') },
    { label: t('wizard.manual.step2Title') },
    { label: t('wizard.manual.step3Title') }
  ];

  return (
    <WizardContext.Provider
      value={{ ...state, setState, orgId, deptId: deptId ?? '', orgSlug, deptSlug }}
    >
      <PageContainer
        title={t('wizard.manual.title')}
        breadcrumbs={[
          { content: <DepartmentBreadcrumb orgSlug={orgSlug} deptSlug={deptSlug} /> },
          { label: t('jobs.title'), href: `/org/${orgSlug}/department/${deptSlug}/jobs` },
          { label: t('wizard.manual.title') }
        ]}
      >
        <WizardStepHeader
          steps={steps}
          currentStepIndex={state.step - 1}
          onStepClick={(displayIndex) => {
            setState((prev) => ({ ...prev, step: (displayIndex + 1) as WizardState['step'] }));
          }}
        />
        <div className="flex flex-1 flex-col">
          {state.step === 1 && <Step1 />}
          {state.step === 2 && <Step2 />}
          {state.step === 3 && <Step3 />}
        </div>
      </PageContainer>
    </WizardContext.Provider>
  );
}
